import { useState } from "react";
import type { MealLogSummaryConfig } from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";
import { ExpenditureCalculator } from "./ExpenditureCalculator";

type Props = WidgetSettingsEditorProps<MealLogSummaryConfig>;

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  allWidgets = [],
}: Props) {
  const [calculatorOpen, setCalculatorOpen] = useState(false);
  const mealLogs = allWidgets.filter((w) => w.kind === "meal-log");
  const expenditure = config.dailyEnergyExpenditure;

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => onChange({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {errors.label ? (
            <p className="field-error" data-testid="widget-error-label">
              {errors.label}
            </p>
          ) : null}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`meal-log-${config.id}`}>
          Matdagbok
        </label>
        <div className="setting-control">
          <select
            id={`meal-log-${config.id}`}
            className="input"
            value={config.mealLogId}
            onChange={(e) => onChange({ mealLogId: e.target.value })}
            data-testid="meal-log-select"
          >
            <option value="">Velg matdagbok…</option>
            {mealLogs.map((w) => (
              <option key={w.id} value={w.id}>
                {/* Disabled instances stay selectable, shown with a hint —
                    the same treatment weekly-point-summary gives them. */}
                {w.label}
                {w.enabled ? "" : " (skjult)"}
              </option>
            ))}
          </select>
          <p className="hint">
            Oversikten summerer én matdagbok. To matdagbøker betyr to
            oversikter.
          </p>
          {mealLogs.length === 0 ? (
            <p className="hint" data-testid="meal-log-select-empty">
              Ingen matdagbok finnes enda — legg til en under Daglig.
            </p>
          ) : null}
          {errors.mealLogId ? (
            <p className="field-error" data-testid="widget-error-meal-log">
              {errors.mealLogId}
            </p>
          ) : null}
        </div>
      </div>

      <div className="setting-row">
        <label className="setting-label" htmlFor={`expenditure-${config.id}`}>
          Energiforbruk
        </label>
        <div className="setting-control">
          <div className="expenditure-row">
            <input
              id={`expenditure-${config.id}`}
              className="input"
              type="number"
              min={1}
              step={10}
              placeholder="kcal/dag"
              value={
                typeof expenditure === "number" && Number.isFinite(expenditure)
                  ? String(expenditure)
                  : ""
              }
              onChange={(e) =>
                onChange({
                  dailyEnergyExpenditure:
                    e.target.value === "" ? undefined : Number(e.target.value),
                })
              }
              data-testid="energy-expenditure-input"
            />
            <button
              type="button"
              className="btn ghost"
              onClick={() => setCalculatorOpen((open) => !open)}
              aria-expanded={calculatorOpen}
              data-testid="open-calculator-button"
            >
              {calculatorOpen ? "Skjul beregning" : "Beregn…"}
            </button>
          </div>
          <p className="hint">
            Hvor mange kalorier kroppen antas å forbrenne per dag. Tomt =
            oversikten viser bare betingelsene.
          </p>
          {errors.dailyEnergyExpenditure ? (
            <p className="field-error" data-testid="widget-error-expenditure">
              {errors.dailyEnergyExpenditure}
            </p>
          ) : null}
          {calculatorOpen ? (
            <ExpenditureCalculator
              config={config}
              onChange={onChange}
              onApply={(value) => {
                onChange({ dailyEnergyExpenditure: value });
                setCalculatorOpen(false);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
